// Recovers from stale-deploy chunk-load failures. After a deploy, a client still
// running the old index.html may lazily import() a chunk whose hashed filename no
// longer exists, throwing "Importing a module script failed" / "Failed to fetch
// dynamically imported module" (BOSKO-FRONTEND-1X). A one-shot reload fetches the
// fresh index.html + chunks. Guarded by the loaded release so a chunk that
// genuinely can't load (persistent network issue) does NOT loop, while a later
// deploy in the same browser session can still recover once.
const RELOAD_KEY = 'chunkReloadRelease';
let reloadInProgressForRelease = null;

export function isChunkLoadError(reason) {
  const message = String(reason?.message ?? reason ?? '');
  return /importing a module script failed|failed to fetch dynamically imported module|error loading dynamically imported module/i.test(
    message,
  );
}

function loadedRelease() {
  return document
    .querySelector('meta[name="app-release"]')
    ?.getAttribute('content');
}

// Loop guard — records the release whose module graph failed before reloading.
// If the reload leaves that same graph active for any reason, it cannot reload
// again. A future deploy has a different loaded release and gets one recovery
// attempt of its own.
export function shouldReloadForChunkError(
  sourceRelease = loadedRelease(),
  storage = sessionStorage,
) {
  if (!sourceRelease) return false;

  try {
    if (storage.getItem(RELOAD_KEY) === sourceRelease) return false;
    storage.setItem(RELOAD_KEY, sourceRelease);
    return true;
  } catch {
    // Without a durable sentinel a reload could loop in private mode or a
    // locked-down WKWebView, so preserve the original error instead.
    return false;
  }
}

// Confirms a chunk-load failure is actually caused by a NEW deploy — the only
// case a reload can fix. The same import failure also happens when an ad-blocker
// blocks a chunk by name or the network drops; reloading then can't help and
// (bounded by the loop guard) just wastes a reload. We compare the release baked
// into the loaded page (<meta name="app-release">, rendered server-side) against
// the server's current release (GET /version) and only treat a genuine mismatch
// as stale. `fetchImpl` is injectable for tests.
async function staleSourceRelease(fetchImpl = (...args) => fetch(...args)) {
  // Offline: never a fresh deploy, and a reload couldn't fetch anything anyway.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return null;
  }

  const loaded = loadedRelease();
  // No release marker (local dev, or SENTRY_RELEASE unset) means the failure
  // cannot be proven stale. Preserve it instead of masking a real 404.
  if (!loaded) return null;

  try {
    const res = await fetchImpl('/version', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.release && data.release !== loaded ? loaded : null;
  } catch {
    // Server unreachable → offline / blocked, not a confirmed deploy.
    return null;
  }
}

export async function confirmStaleDeploy(
  fetchImpl = (...args) => fetch(...args),
) {
  return Boolean(await staleSourceRelease(fetchImpl));
}

export async function reloadForChunkError(
  reason,
  {
    fetchImpl = (...args) => fetch(...args),
    reload = () => window.location.reload(),
    storage = sessionStorage,
  } = {},
) {
  if (!isChunkLoadError(reason) && !isLazyModuleResolutionError(reason)) {
    return false;
  }

  // Confirm a real deploy before consuming the loop guard or reloading, so a
  // blocked/offline import no longer forces a pointless reload.
  const sourceRelease = await staleSourceRelease(fetchImpl);
  if (!sourceRelease) return false;

  // vite:preloadError fires before the import promise rejects. Both the global
  // listener and the import boundary can therefore reach this function for the
  // same failure. Treat the second path as handled while the first reload is in
  // flight, but do not issue another reload.
  if (reloadInProgressForRelease === sourceRelease) {
    try {
      if (storage.getItem(RELOAD_KEY) === sourceRelease) return true;
    } catch {
      return false;
    }
    // The sentinel disappeared without a document reload (possible in tests or
    // if application code explicitly clears sessionStorage), so this is no
    // longer the in-flight recovery we recorded.
    reloadInProgressForRelease = null;
  }
  if (!shouldReloadForChunkError(sourceRelease, storage)) return false;

  reloadInProgressForRelease = sourceRelease;
  reload();
  return true;
}

// The resolved-to-undefined variant of a stale-deploy chunk mismatch: after a
// deploy, a lazily imported chunk can load (HTTP 200) yet resolve to a module
// without the expected default export. React's lazy() initializer then throws
// "Cannot read properties of undefined (reading 'default')" synchronously
// during render (BOSKO-FRONTEND-4B/4C). Unlike a failed fetch this is NOT an
// import rejection, so vite:preloadError / unhandledrejection never fire — it
// surfaces as a render error caught by the React error boundary instead.
export function isLazyModuleResolutionError(reason) {
  const message = String(reason?.message ?? reason ?? '');
  return /reading '?default'?/i.test(message);
}

// Recovery hook for the top-level React error boundary. Treats both classic
// chunk-load failures and the resolved-to-undefined lazy variant as stale-deploy
// signals and triggers the same guarded one-shot reload. The release-keyed loop
// guard means a genuine (non-deploy) render bug falls through to the boundary's
// fallback UI instead of looping.
export function reloadForBoundaryError(error) {
  if (isChunkLoadError(error) || isLazyModuleResolutionError(error)) {
    return reloadForChunkError(error);
  }
  return undefined;
}

// Handle the rejection at the import boundary, before it becomes an unhandled
// promise rejection (and before Sentry reports a recovered stale deploy). Only
// a confirmed stale deploy is held pending while navigation starts; every
// other failure is rethrown unchanged to its normal error path.
export async function importWithChunkReload(importModule, options) {
  try {
    return await importModule();
  } catch (error) {
    if (await reloadForChunkError(error, options)) {
      return new Promise(() => {});
    }
    throw error;
  }
}

export function installChunkReloadHandler() {
  // Vite fires this for failed module preloads (covers most lazy imports).
  //
  // Do NOT call event.preventDefault() here. Vite's __vitePreload treats a
  // prevented vite:preloadError as "handled" and RESOLVES the import to
  // `undefined` instead of rejecting — which silently defeats every lazy
  // import's own `.catch` fallback and hands `undefined` to React.lazy, so a
  // single blocked chunk (e.g. an ad-blocker blocking a chunk by name) crashes
  // the whole app with "reading 'default'" (BOSKO-FRONTEND-4C). Letting Vite
  // re-throw lets those per-import fallbacks run; we still trigger the reload.
  window.addEventListener('vite:preloadError', (event) => {
    reloadForChunkError(
      event.payload ?? 'Failed to fetch dynamically imported module',
    );
  });
  // Catch the raw dynamic-import rejection too (non-preloaded imports).
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) reloadForChunkError(event.reason);
  });
}
